import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';

import {
    Box,
    Grid,
    Chip,
    Stack,
    Button,
    Tooltip,
    Typography,
    CircularProgress,
} from '@mui/material';

import { Modal, SnackBarAlert } from 'shared';
import RefundMatched from '../RefundMatched';
import TransactionCard from './TransactionCard';
import ModalPaymentPlans from './ModalPaymentPlans';
import TransactionSummary from './TransactionSummary';
import {
    aggInventorySchema,
    aggInventoryInitialValues,
} from 'components/Inventory/utils/aggInventorySchema';
import { useCustomMediaQuery } from 'hooks/useMediaQuery';
import ModalPurchaseActivity from './ModalPurchaseActivity';
import { useInventoryService } from 'store/services/inventory.service';
import { usePaymentPlanService } from 'store/services/paymentPlan.service';
import { matchWithAggregatedFileInventoryStyles } from 'components/Inventory/style/style';

const MatchWithAggregatedFileInventory = ({
    transaction,
    showMatchingModal,
    setShowMatchingModal,
    onMatchSuccess,
}) => {
    const classes = matchWithAggregatedFileInventoryStyles();
    const isMobileScreen = useCustomMediaQuery('(max-width: 600px)');
    const { id: companyId } = useSelector((state) => state.company.data);
    const { matchPlaidTransactionWithPaymentPlan } = usePaymentPlanService();
    const { getAggregatedFileInventories, matchAggFileInventoryWithPlaidTransaction, matchRefund } =
        useInventoryService();

    const [page, setPage] = useState(1);
    const [checked, setChecked] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [tableLoading, setTableLoading] = useState(true);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedTabId, setSelectedTabId] = useState('purchase');
    const [remainingAmount, setRemainingAmount] = useState(0.0);
    const [matchButtonDisable, setMatchButtonDisable] = useState(true);
    const [totalAmountSelected, setTotalAmountSelected] = useState(0.0);
    const [matchButtonLoading, setMatchButtonLoading] = useState(false);
    const [unmatchedInventories, setUnmatchedInventories] = useState(null);
    const [selectedPaymentPlans, setSelectedPaymentPlans] = useState([]);

    const handleRefundSelection = (selected) => {
        setSelectedRows(selected);
        setMatchButtonDisable(selected.length === 0);

        const selectedRowsSum = selected.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);

        const remaining = (Math.abs(transaction.amount) - Math.abs(selectedRowsSum)).toFixed(2);

        setTotalAmountSelected(selectedRowsSum);
        setRemainingAmount(remaining);

        if (selected.length === 1 && checked) {
            setMatchButtonDisable(false);
        } else {
            setMatchButtonDisable(remaining != 0);
        }
    };

    const changeTab = (tab) => {
        setSelectedTabId(tab.id);

        setSelectedRows([]);
        setRemainingAmount(0.0);
        setMatchButtonDisable(true);
        setSelectedOption(null);
        setTotalAmountSelected(0.0);
        setPage(1);
        setSelectedPaymentPlans([]);
        if (tab.id === 'purchase') {
            handleGetAggregatedFileInventories(1, {});
        }
    };

    useEffect(() => {
        handleGetAggregatedFileInventories(page, {});
    }, []);

    const handleGetAggregatedFileInventories = async (page, filters) => {
        try {
            setTableLoading(true);
            const response = await getAggregatedFileInventories({
                companyId,
                page,
                isReviewed: 'False',
                description: filters.description,
                startDate: filters.startDate
                    ? dayjs(filters.startDate).format('YYYY-MM-DD')
                    : undefined,
                endDate: filters.endDate ? dayjs(filters.endDate).format('YYYY-MM-DD') : undefined,
                expiresAt: filters.expires_at
                    ? dayjs(filters.expires_at).format('YYYY-MM-DD')
                    : undefined,
                amount: filters.amount,
                purchaseId: filters.purchaseId,
                referenceId: filters.referenceId,
                venue: filters.venue,
            });
            setUnmatchedInventories(response);
        } catch (error) {
            console.error(error);
        } finally {
            setTableLoading(false);
        }
    };

    const handlePageChange = (page, filters) => {
        setPage(page);
        handleGetAggregatedFileInventories(page, filters);
    };

    const handleCheckboxChange = (inventory) => {
        setSelectedRows((prevSelected) => {
            const isSelected = prevSelected.some((item) => item.id === inventory.id);
            let updatedSelectedRows;

            if (isSelected) {
                updatedSelectedRows = prevSelected.filter((item) => item.id !== inventory.id);
            } else {
                updatedSelectedRows = [...prevSelected, inventory];
            }

            const selectedRowsSum = updatedSelectedRows.reduce(
                (sum, row) => sum + parseFloat(row.purchase_price),
                0,
            );

            const amount = (Math.abs(transaction.amount) - Math.abs(selectedRowsSum)).toFixed(2);

            setTotalAmountSelected(selectedRowsSum);
            setRemainingAmount(amount);
            setSelectedOption(null);

            if (updatedSelectedRows.length === 1 && checked) {
                setMatchButtonDisable(false);
            } else {
                setMatchButtonDisable(amount != 0);
            }

            return updatedSelectedRows;
        });
    };

    const handleSelectedOptionChange = (value) => {
        setSelectedOption(value);
        setMatchButtonDisable(false);
    };

    const handleCheckedChange = (value) => {
        setChecked(value);
        if (value && selectedRows.length === 1) {
            setMatchButtonDisable(false);
        }
        if (!value) {
            setMatchButtonDisable(true);
        }
    };

    const handleMatchClick = async (modalType) => {
        try {
            setMatchButtonLoading(true);

            if (modalType === 'payment_plans') {
                const data = {
                    plaid_transaction_id: transaction.id,
                    payment_plan_id: selectedPaymentPlans[0].id,
                    company_id: companyId,
                };

                await matchPlaidTransactionWithPaymentPlan(data);
            } else if (modalType === 'refund') {
                const data = {
                    company_id: companyId,
                    plaidTransactionId: transaction.id,
                    aggregatedFileInventoryIds: selectedRows[0].aggregated_file_inventories.map(
                        (item) => item.id,
                    ),
                };
                await matchRefund(data);
            } else {
                const data = {
                    plaidTransactionId: transaction.id,
                    adjustedPrice: remainingAmount != 0 ? remainingAmount : null,
                    aggregatedFileInventoryIds: selectedRows.map((row) => row.id),
                    adjustedAggregatedFileInventoryId:
                        remainingAmount != 0
                            ? selectedRows.length == 1
                                ? selectedRows[0]?.id
                                : selectedOption.value
                            : null,
                    companyId,
                };
                await matchAggFileInventoryWithPlaidTransaction(data);
            }

            setShowMatchingModal(false);
            if (typeof onMatchSuccess === 'function') {
                onMatchSuccess();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setMatchButtonLoading(false);
        }
    };

    const handleClearClick = (handleReset) => {
        setPage(1);
        handleReset();
        handleGetAggregatedFileInventories(1, {});
    };

    const formatRowData = (item) => {
        if (!item) return '';

        const fieldsToShow = [
            'description',
            'quantity',
            'unit_price',
            'total_price',
            'date',
            'reference_id',
            'status',
            'notes',
        ];

        return fieldsToShow
            .filter((key) => key in item)
            .map((key) => {
                const formattedKey = key
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (l) => l.toUpperCase());
                let value = item[key];

                if (value === null || value === undefined || value === '') {
                    value = 'N/A';
                } else if (
                    typeof value === 'string' &&
                    dayjs(value, 'YYYY-MM-DD', true).isValid()
                ) {
                    value = dayjs(value).format('MMM D, YYYY');
                } else if (
                    typeof value === 'number' &&
                    (key.includes('price') || key.includes('amount'))
                ) {
                    value = `$${value.toFixed(2)}`;
                } else if (typeof value === 'boolean') {
                    value = value ? 'Yes' : 'No';
                }

                return `${formattedKey}: ${value}`;
            })
            .join('\n');
    };

    const SelectedItemsChips = ({ items, onRemove }) => {
        if (!items.length) return null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2,
                    alignItems: 'center',
                    p: 1,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                }}
            >
                <Typography variant="subtitle2" color="textSecondary" sx={{ mr: 1 }}>
                    Selected Items:
                </Typography>
                {items.map((item) => (
                    <Tooltip
                        key={item.id}
                        title={<Box sx={{ whiteSpace: 'pre-line' }}>{formatRowData(item)}</Box>}
                        arrow
                        placement="right"
                    >
                        <Box display="inline-block">
                            <Chip
                                label={`#${item.purchase_id || item.id} ($${parseFloat(
                                    item.purchase_price || 0,
                                ).toFixed(2)})`}
                                onDelete={(e) => {
                                    e.stopPropagation();
                                    onRemove(item);
                                }}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiChip-deleteIcon': {
                                        color: 'primary.main',
                                        '&:hover': {
                                            color: 'primary.dark',
                                        },
                                    },
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                            />
                        </Box>
                    </Tooltip>
                ))}
            </Box>
        );
    };

    const getTabLabel = (tab) => {
        const count = tab.id === 'purchase' ? selectedRows.length : selectedPaymentPlans.length;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 160 }}>
                <span>{tab.label}</span>
                {count > 0 && (
                    <Box
                        sx={{
                            ml: 1,
                            minWidth: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                        }}
                    >
                        {count}
                    </Box>
                )}
            </Box>
        );
    };

    useEffect(() => {
        handleGetAggregatedFileInventories(page, {});
    }, []);

    const modalTabsData = [
        {
            id: 'purchase',
            label: 'Purchases',
            getLabel: () => getTabLabel({ id: 'purchase', label: 'Purchases' }),
            component: (
                <ModalPurchaseActivity
                    initialValues={aggInventoryInitialValues}
                    validationSchema={aggInventorySchema}
                    unmatchedInventories={unmatchedInventories}
                    selectedRows={selectedRows}
                    handleCheckboxChange={handleCheckboxChange}
                    tableLoading={tableLoading}
                    page={page}
                    handlePageChange={handlePageChange}
                    handleGetAggregatedFileInventories={handleGetAggregatedFileInventories}
                    isMobileScreen={isMobileScreen}
                    handleClearClick={handleClearClick}
                />
            ),
        },
        {
            id: 'payment_plans',
            label: 'Payment Plans',
            getLabel: () => getTabLabel({ id: 'payment_plans', label: 'Payment Plans' }),
            component: (
                <ModalPaymentPlans
                    setMatchButtonDisable={setMatchButtonDisable}
                    setSelectedPaymentPlans={setSelectedPaymentPlans}
                    selectedPaymentPlans={selectedPaymentPlans}
                />
            ),
        },
        {
            id: 'refund',
            label: 'Refund',
            getLabel: () => getTabLabel({ id: 'refund', label: 'Refund' }),
            component: (
                <RefundMatched
                    selectedRows={selectedRows}
                    onSelectionChange={handleRefundSelection}
                />
            ),
        },
    ];

    return (
        <>
            <Modal
                fullWidth={true}
                open={showMatchingModal}
                title="Select Activity"
                handleClose={() => setShowMatchingModal(false)}
                customWidth="95%"
                showFooter
                footer={
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="outlined"
                            className={classes.cancelButton}
                            onClick={() => setShowMatchingModal(false)}
                            disabled={matchButtonLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={matchButtonDisable || matchButtonLoading}
                            onClick={() => handleMatchClick(selectedTabId)}
                            className={matchButtonLoading ? classes.loadingButton : ''}
                            startIcon={
                                matchButtonLoading && <CircularProgress size={20} color="inherit" />
                            }
                        >
                            Match
                        </Button>
                    </Box>
                }
            >
                <Grid container spacing={2} alignItems="center" marginBottom="10px">
                    <Grid item xs={12} sm={4} md={3}>
                        <TransactionSummary
                            checked={checked}
                            transaction={transaction}
                            selectedRows={selectedRows}
                            selectedOption={selectedOption}
                            remainingAmount={remainingAmount}
                            numSelected={selectedRows.length}
                            setSelectedOption={setSelectedOption}
                            totalAmountSelected={totalAmountSelected}
                            handleCheckedChange={handleCheckedChange}
                            setMatchButtonDisable={setMatchButtonDisable}
                            handleSelectedOptionChange={handleSelectedOptionChange}
                            selectedTabId={selectedTabId}
                        />
                    </Grid>

                    <Grid item xs={12} sm={8} md={9}>
                        <TransactionCard transaction={transaction} />
                    </Grid>
                </Grid>

                <Box padding={1} sx={classes.menuWrapper}>
                    <Stack
                        display="flex"
                        flexDirection={{ xs: 'column', md: 'row' }}
                        gap={1}
                        sx={classes.stack}
                        alignItems="center"
                    >
                        {modalTabsData.map((tab) => (
                            <Grid item key={tab.id} borderRadius={'5px'}>
                                <Button
                                    onClick={() => changeTab(tab)}
                                    sx={classes.tabButton(selectedTabId === tab.id)}
                                    variant={selectedTabId === tab.id ? 'contained' : 'text'}
                                >
                                    {tab.label}
                                </Button>
                            </Grid>
                        ))}
                    </Stack>
                </Box>

                {selectedTabId === 'purchase' && selectedRows.length > 0 && (
                    <SelectedItemsChips items={selectedRows} onRemove={handleCheckboxChange} />
                )}

                <Box
                    padding={2}
                    sx={{
                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                            ? theme.backgroundColor.primary
                            : 'rgb(244, 244, 246)',
                        borderRadius: '8px',
                        marginTop: '16px',
                    }}
                >
                    {modalTabsData.find((tab) => tab.id === selectedTabId)?.component}
                </Box>
            </Modal>
        </>
    );
};

export default MatchWithAggregatedFileInventory;
